import React from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';
import { TitleWithDescription, LbRadioGroup, RadioOption } from '@infoods/lifebase-ui';

import { ButtonsRow } from '../../components/ButtonsRow/ButtonsRow';
import { InfoBox } from '../../../../../shared/components/ui-components';
import { setupEntityStore } from '../../../../stores/setupEntityStore';
import {
  BILLING_TYPE,
  BillingType,
} from '../../../../../shared/constants/user-info/billingType';
import { paymentService } from '../../../../../shared/services/paymentService';

type PaymentTypeFormValue = {
  billingType: BillingType | null;
};

const PaymentTypeStep = observer(() => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<PaymentTypeFormValue>();

  const { entityId, stepperMethods, solutionTypeFlags, setBillingType } =
    setupEntityStore;
  const { goToNextStep } = stepperMethods;

  const handleFormSubmit = handleSubmit(async ({ billingType }) => {
    if (!entityId) {
      throw new Error('Entity id is not defined');
    }

    if (!billingType) {
      enqueueSnackbar('Please select a payment type to proceed further.', {
        variant: 'error',
        autoHideDuration: 2000,
      });
      return;
    }

    const { billingType: updatedBillingType } = await paymentService.updateBillingType(
      entityId,
      billingType,
    );
    setBillingType(updatedBillingType);
    goToNextStep();
  });

  const billingTypeOptions: RadioOption<BillingType>[] = [
    {
      label: (
        <TitleWithDescription
          title="Automated Billing Payment"
          description="Customers pay for their subscriptions during self-registration"
          titleFont="title-16-bold"
        />
      ),
      value: BILLING_TYPE.AUTOMATED_BILLING,
    },
    {
      label: (
        <TitleWithDescription
          title="Self-Billing Payment"
          description={
            <span>
              In this case, an entity will take payment from nutrition clients however
              they choose; i.e. cash, check, or other payment processor.
              <br />
              The entity will be automatically debited for each active user on a monthly
              basis.
            </span>
          }
          titleFont="title-16-bold"
        />
      ),
      value: BILLING_TYPE.SELF_BILLING,
      // self-billing is available only for nutrition solution (nutrition & scan_iq) but not for scan_iq only
      disabled: !solutionTypeFlags.NUTRITION,
    },
  ];

  const availableOptions = billingTypeOptions.filter((option) => !option.disabled);
  const isOnlyOneOption = availableOptions.length === 1;
  const defaultValue = isOnlyOneOption ? availableOptions[0].value : null;

  return (
    <form onSubmit={handleFormSubmit} noValidate>
      <InfoBox title="Payment Type">
        <LbRadioGroup
          control={control}
          name="billingType"
          options={billingTypeOptions}
          align="start"
          rules={{ required: 'Please select a payment type.' }}
          fullWidth
          defaultValue={defaultValue}
        />
      </InfoBox>
      <ButtonsRow yesDisabled={!isValid || isSubmitting} />
    </form>
  );
});

export { PaymentTypeStep };
